import Phaser from 'phaser';
import type { AnimalKey } from '../types';

type Build = (scene: Phaser.Scene) => Phaser.GameObjects.Container;

export function makeAnimal(
  scene: Phaser.Scene,
  key: AnimalKey,
  x: number,
  y: number,
): Phaser.GameObjects.Container {
  const c = BUILDERS[key](scene);
  c.x = x;
  c.y = y;
  return c;
}

// Helpers ------------------------------------------------------------

function eye(s: Phaser.Scene, x: number, y: number, color = 0x111111, white = true) {
  const parts: Phaser.GameObjects.GameObject[] = [];
  if (white) parts.push(s.add.circle(x, y, 3, 0xffffff));
  parts.push(s.add.circle(x + 0.4, y + 0.2, 1.6, color));
  return parts;
}

function hoof(s: Phaser.Scene, x: number, y: number, color = 0x2b1d0e) {
  return s.add.rectangle(x, y, 10, 4, color);
}

// Builders -----------------------------------------------------------

const BUILDERS: Record<AnimalKey, Build> = {
  // ───────────────── Land ─────────────────
  horse: (s) => {
    const c = s.add.container(0, 0);
    // legs (back darker)
    c.add(s.add.rectangle(-26, 30, 10, 30, 0x6b3e1a));
    c.add(s.add.rectangle(-12, 32, 10, 30, 0x6b3e1a));
    c.add(s.add.rectangle(12, 32, 10, 30, 0x8a5230));
    c.add(s.add.rectangle(26, 30, 10, 30, 0x8a5230));
    // hooves
    c.add(hoof(s, -26, 46));
    c.add(hoof(s, -12, 48));
    c.add(hoof(s, 12, 48));
    c.add(hoof(s, 26, 46));
    // tail (attached at back of body)
    const tail = s.add.graphics();
    tail.fillStyle(0x2b1d0e, 1);
    tail.fillTriangle(-46, 4, -64, 26, -42, 22);
    tail.fillTriangle(-46, 0, -60, 14, -42, 16);
    c.add(tail);
    // body
    c.add(s.add.ellipse(0, 6, 96, 52, 0x8a5230));
    c.add(s.add.ellipse(0, 14, 70, 26, 0xa86a3a));
    // chest
    c.add(s.add.ellipse(28, 10, 38, 40, 0x8a5230));
    // neck — angled forward
    const neck = s.add.graphics();
    neck.fillStyle(0x8a5230, 1);
    neck.fillTriangle(36, -6, 60, -22, 50, 18);
    c.add(neck);
    // head — sits at top of neck
    c.add(s.add.ellipse(58, -22, 28, 22, 0x8a5230));
    // muzzle
    c.add(s.add.ellipse(70, -16, 16, 12, 0xa86a3a));
    c.add(s.add.circle(74, -18, 1, 0x222222));
    c.add(s.add.circle(74, -14, 1, 0x222222));
    // mane along neck top
    const mane = s.add.graphics();
    mane.fillStyle(0x2b1d0e, 1);
    mane.fillTriangle(38, -16, 50, -28, 44, -10);
    mane.fillTriangle(46, -22, 56, -32, 52, -16);
    mane.fillTriangle(28, -2, 40, -10, 32, 6);
    c.add(mane);
    // ear
    c.add(s.add.triangle(50, -32, 0, 0, -3, -7, 4, -3, 0x6b3e1a));
    // eye on head
    c.add(eye(s, 60, -24));
    // mouth line
    const m = s.add.graphics();
    m.lineStyle(1.4, 0x402610, 1);
    m.beginPath();
    m.moveTo(72, -12);
    m.lineTo(78, -12);
    m.strokePath();
    c.add(m);
    return c;
  },


  pig: (s) => {
    const c = s.add.container(0, 0);
    // tail (curly)
    const tail = s.add.graphics();
    tail.lineStyle(4, 0xe089a8, 1);
    tail.beginPath();
    tail.arc(-48, -6, 8, 0, Math.PI * 1.6, false);
    tail.strokePath();
    c.add(tail);
    // legs
    c.add(s.add.rectangle(-30, 26, 14, 22, 0xe089a8));
    c.add(s.add.rectangle(-14, 28, 14, 22, 0xe089a8));
    c.add(s.add.rectangle(10, 28, 14, 22, 0xe089a8));
    c.add(s.add.rectangle(26, 26, 14, 22, 0xe089a8));
    // hooves
    c.add(hoof(s, -30, 38, 0x6b2a3a));
    c.add(hoof(s, -14, 40, 0x6b2a3a));
    c.add(hoof(s, 10, 40, 0x6b2a3a));
    c.add(hoof(s, 26, 38, 0x6b2a3a));
    // body
    c.add(s.add.ellipse(0, 0, 92, 64, 0xf4a6c0));
    c.add(s.add.ellipse(5, 8, 62, 36, 0xfcc9d9));
    // head
    c.add(s.add.circle(36, -8, 26, 0xf4a6c0));
    // snout
    c.add(s.add.ellipse(54, 0, 20, 16, 0xe089a8));
    c.add(s.add.circle(56, -2, 1.6, 0x6b2a3a));
    c.add(s.add.circle(56, 4, 1.6, 0x6b2a3a));
    // ears
    c.add(s.add.triangle(24, -28, 0, 0, 14, -2, 4, 14, 0xe089a8));
    c.add(s.add.triangle(42, -30, 0, 0, 14, -2, 4, 14, 0xe089a8));
    // eye
    c.add(eye(s, 40, -14));
    // mouth
    const pm = s.add.graphics();
    pm.lineStyle(1.4, 0x6b2a3a, 1);
    pm.beginPath();
    pm.moveTo(46, 6);
    pm.lineTo(54, 6);
    pm.strokePath();
    c.add(pm);
    return c;
  },

  sheep: (s) => {
    const c = s.add.container(0, 0);
    c.add(s.add.rectangle(-22, 28, 8, 24, 0x2c2c2c));
    c.add(s.add.rectangle(-8, 30, 8, 24, 0x2c2c2c));
    c.add(s.add.rectangle(8, 30, 8, 24, 0x2c2c2c));
    c.add(s.add.rectangle(22, 28, 8, 24, 0x2c2c2c));
    // tail puff
    c.add(s.add.circle(-40, 4, 8, 0xeeeeee));
    // wool body
    const wool = s.add.graphics();
    wool.fillStyle(0xeeeeee, 1);
    for (const [bx, by, br] of [
      [-30, 0, 18],
      [-12, -8, 22],
      [10, -10, 22],
      [28, -2, 18],
      [-20, 12, 18],
      [4, 14, 20],
      [22, 12, 18],
    ] as [number, number, number][]) {
      wool.fillCircle(bx, by, br);
    }
    c.add(wool);
    // shadow under wool
    wool.fillStyle(0xc7c7c7, 0.3);
    wool.fillCircle(-12, 16, 8);
    wool.fillCircle(14, 18, 10);
    // head
    c.add(s.add.ellipse(36, -4, 24, 22, 0x2c2c2c));
    c.add(s.add.ellipse(46, 2, 14, 9, 0xc7bda5));
    // ears
    c.add(s.add.ellipse(28, -14, 9, 5, 0x2c2c2c));
    c.add(s.add.ellipse(44, -14, 9, 5, 0x2c2c2c));
    // wool fringe
    c.add(s.add.circle(30, -10, 5, 0xeeeeee));
    // eye
    c.add(eye(s, 40, -6, 0xffffff, false));
    c.add(s.add.circle(40, -6, 1.6, 0x111111));
    return c;
  },

  donkey: (s) => {
    const c = s.add.container(0, 0);
    c.add(s.add.rectangle(-24, 30, 9, 28, 0x9ea0a8));
    c.add(s.add.rectangle(-8, 32, 9, 28, 0x9ea0a8));
    c.add(s.add.rectangle(10, 32, 9, 28, 0x9ea0a8));
    c.add(s.add.rectangle(26, 30, 9, 28, 0x9ea0a8));
    c.add(hoof(s, -24, 44));
    c.add(hoof(s, -8, 46));
    c.add(hoof(s, 10, 46));
    c.add(hoof(s, 26, 44));
    // tail with tuft
    c.add(s.add.rectangle(-46, 6, 4, 18, 0x9ea0a8));
    c.add(s.add.circle(-46, 18, 5, 0x2b1d0e));
    // body
    c.add(s.add.ellipse(0, 6, 90, 50, 0x9ea0a8));
    c.add(s.add.ellipse(0, 14, 64, 22, 0xc8c9d0));
    // chest + neck
    c.add(s.add.ellipse(28, 4, 30, 34, 0x9ea0a8));
    // head
    c.add(s.add.ellipse(46, -14, 30, 22, 0x9ea0a8));
    c.add(s.add.ellipse(58, -8, 14, 10, 0xb0b2bc));
    c.add(s.add.circle(60, -10, 1, 0x222222));
    c.add(s.add.circle(60, -6, 1, 0x222222));
    // long ears
    c.add(s.add.ellipse(38, -28, 8, 22, 0x9ea0a8));
    c.add(s.add.ellipse(50, -28, 8, 22, 0x9ea0a8));
    c.add(s.add.ellipse(38, -28, 4, 18, 0xc8c9d0));
    c.add(s.add.ellipse(50, -28, 4, 18, 0xc8c9d0));
    // mane
    const dm = s.add.graphics();
    dm.fillStyle(0x2b1d0e, 1);
    dm.fillTriangle(28, -8, 36, -18, 32, -2);
    c.add(dm);
    // eye
    c.add(eye(s, 50, -16));
    return c;
  },

  // ───────────────── Aquatic small ─────────────────
  duck: (s) => {
    const c = s.add.container(0, 0);
    // legs
    c.add(s.add.rectangle(-6, 28, 4, 14, 0xe0a830));
    c.add(s.add.rectangle(8, 28, 4, 14, 0xe0a830));
    // webbed feet
    c.add(s.add.ellipse(-6, 36, 14, 4, 0xe0a830));
    c.add(s.add.ellipse(8, 36, 14, 4, 0xe0a830));
    // body
    c.add(s.add.ellipse(0, 4, 84, 52, 0xffffff));
    // wing
    c.add(s.add.ellipse(-8, 4, 38, 26, 0xeaeaea));
    // tail
    c.add(s.add.triangle(-44, -2, 0, 0, -12, -8, -12, 8, 0xffffff));
    // head
    c.add(s.add.circle(30, -14, 18, 0xffffff));
    // bill (top + bottom)
    c.add(s.add.ellipse(46, -10, 18, 8, 0xe0a830));
    c.add(s.add.rectangle(46, -7, 18, 1, 0xb88420));
    // eye
    c.add(eye(s, 32, -18));
    return c;
  },

  otter: (s) => {
    const c = s.add.container(0, 0);
    // legs
    c.add(s.add.rectangle(-22, 28, 8, 18, 0x8a5230));
    c.add(s.add.rectangle(-6, 30, 8, 18, 0x8a5230));
    c.add(s.add.rectangle(10, 30, 8, 18, 0x8a5230));
    c.add(s.add.rectangle(26, 28, 8, 18, 0x8a5230));
    // long thick tail
    c.add(s.add.ellipse(-44, 8, 30, 12, 0x8a5230));
    c.add(s.add.circle(-58, 6, 6, 0x6b3e1a));
    // body
    c.add(s.add.ellipse(0, 4, 92, 46, 0x8a5230));
    c.add(s.add.ellipse(0, 14, 60, 22, 0xc7bda5));
    // head
    c.add(s.add.circle(36, -8, 22, 0x8a5230));
    // muzzle
    c.add(s.add.ellipse(48, -2, 16, 12, 0xc7bda5));
    c.add(s.add.circle(50, -6, 1.6, 0x111111));
    // ears (small round)
    c.add(s.add.circle(28, -22, 5, 0x8a5230));
    c.add(s.add.circle(42, -22, 5, 0x8a5230));
    c.add(s.add.circle(28, -22, 2.4, 0xc7bda5));
    c.add(s.add.circle(42, -22, 2.4, 0xc7bda5));
    // eye
    c.add(eye(s, 40, -12));
    // whiskers
    const w = s.add.graphics();
    w.lineStyle(1, 0x402610, 1);
    w.beginPath();
    w.moveTo(50, -2);
    w.lineTo(60, 0);
    w.moveTo(50, 0);
    w.lineTo(60, 4);
    w.strokePath();
    c.add(w);
    return c;
  },

  clownfish: (s) => {
    const c = s.add.container(0, 0);
    // body
    c.add(s.add.ellipse(0, 0, 84, 60, 0xe66a1a));
    // white stripes
    c.add(s.add.rectangle(-18, 0, 8, 50, 0xffffff));
    c.add(s.add.rectangle(8, 0, 8, 54, 0xffffff));
    // black trim around stripes
    c.add(s.add.rectangle(-22, 0, 1.5, 48, 0x111111));
    c.add(s.add.rectangle(-14, 0, 1.5, 48, 0x111111));
    c.add(s.add.rectangle(4, 0, 1.5, 52, 0x111111));
    c.add(s.add.rectangle(12, 0, 1.5, 52, 0x111111));
    // tail fin
    c.add(s.add.triangle(-46, 0, 0, 0, -18, -18, -18, 18, 0xe66a1a));
    c.add(s.add.triangle(-42, 0, 0, 0, -10, -10, -10, 10, 0xffffff));
    // top fin
    c.add(s.add.triangle(0, -32, 0, 0, -14, 8, 14, 8, 0xe66a1a));
    c.add(s.add.triangle(0, -32, 0, 0, -10, 4, 10, 4, 0x111111));
    // bottom fin
    c.add(s.add.triangle(0, 32, 0, 0, -10, -6, 10, -6, 0xe66a1a));
    // side fin
    c.add(s.add.ellipse(8, 14, 14, 8, 0xe66a1a));
    // eye
    c.add(s.add.circle(22, -8, 5, 0xffffff));
    c.add(s.add.circle(22, -8, 2.5, 0x111111));
    // mouth (small smile)
    const mouth = s.add.graphics();
    mouth.lineStyle(1.6, 0x6b1a1a, 1);
    mouth.beginPath();
    mouth.moveTo(32, 4);
    mouth.lineTo(38, 6);
    mouth.lineTo(32, 8);
    mouth.strokePath();
    c.add(mouth);
    return c;
  },

  // ───────────────── Aquatic big ─────────────────
  stingray: (s) => {
    const c = s.add.container(0, 0);
    // diamond body (top + bottom triangles)
    const body = s.add.graphics();
    body.fillStyle(0x6a6f80, 1);
    body.fillTriangle(-66, 0, 0, -24, 66, 0);
    body.fillTriangle(-66, 0, 0, 22, 66, 0);
    c.add(body);
    // top highlight
    const hi = s.add.graphics();
    hi.fillStyle(0x8a92a8, 1);
    hi.fillTriangle(-44, -2, 0, -16, 44, -2);
    c.add(hi);
    // spots
    const sp = s.add.graphics();
    sp.fillStyle(0x40455a, 1);
    sp.fillCircle(-18, -6, 3);
    sp.fillCircle(20, -8, 3);
    sp.fillCircle(0, -2, 3);
    c.add(sp);
    // eyes (top of head)
    c.add(s.add.circle(-12, -10, 2, 0x111111));
    c.add(s.add.circle(12, -10, 2, 0x111111));
    // tail with barb
    c.add(s.add.rectangle(60, 0, 30, 4, 0x6a6f80));
    c.add(s.add.triangle(80, 0, 0, 0, -6, -4, -6, 4, 0x40455a));
    return c;
  },

  turtle: (s) => {
    const c = s.add.container(0, 0);
    // flippers (back)
    c.add(s.add.ellipse(-30, 18, 24, 14, 0x4ea36b));
    c.add(s.add.ellipse(30, 18, 24, 14, 0x4ea36b));
    // flippers (front)
    c.add(s.add.ellipse(-30, -8, 20, 12, 0x4ea36b));
    c.add(s.add.ellipse(30, -8, 20, 12, 0x4ea36b));
    // shell outer
    c.add(s.add.ellipse(0, 0, 100, 64, 0x6b3e1a));
    // shell inner
    c.add(s.add.ellipse(0, 0, 84, 50, 0x8a5230));
    // hex pattern
    const pat = s.add.graphics();
    pat.lineStyle(2, 0x4d3520, 1);
    for (const px of [-22, 0, 22]) {
      pat.strokeCircle(px, -10, 8);
      pat.strokeCircle(px, 10, 8);
    }
    c.add(pat);
    // head + neck
    c.add(s.add.ellipse(46, 0, 22, 18, 0x4ea36b));
    c.add(s.add.circle(50, -4, 12, 0x4ea36b));
    // eye
    c.add(eye(s, 54, -6));
    // mouth
    const tm = s.add.graphics();
    tm.lineStyle(1.4, 0x224d1a, 1);
    tm.beginPath();
    tm.moveTo(56, 0);
    tm.lineTo(60, 0);
    tm.strokePath();
    c.add(tm);
    // tail
    c.add(s.add.triangle(-50, 4, 0, 0, -10, -3, -10, 3, 0x4ea36b));
    return c;
  },


  seal: (s) => {
    const c = s.add.container(0, 0);
    // body
    c.add(s.add.ellipse(0, 4, 100, 52, 0x6e7682));
    // belly
    c.add(s.add.ellipse(0, 14, 64, 20, 0xc8c9d0));
    // back flippers
    c.add(s.add.triangle(-50, 8, 0, 0, -14, -8, -14, 14, 0x6e7682));
    // side flipper
    c.add(s.add.ellipse(-12, 22, 22, 8, 0x6e7682));
    // head (rounder, sits in front)
    c.add(s.add.circle(40, -8, 20, 0x6e7682));
    // muzzle
    c.add(s.add.ellipse(54, -4, 16, 10, 0x9aa0aa));
    c.add(s.add.circle(58, -6, 1.4, 0x111111));
    // eyes (big and friendly)
    c.add(s.add.circle(36, -12, 3, 0xffffff));
    c.add(s.add.circle(36, -12, 1.6, 0x111111));
    // whiskers
    const w = s.add.graphics();
    w.lineStyle(1, 0x2c2c2c, 1);
    w.beginPath();
    w.moveTo(54, 0);
    w.lineTo(64, 2);
    w.moveTo(54, 2);
    w.lineTo(64, 6);
    w.strokePath();
    c.add(w);
    // mouth
    const m = s.add.graphics();
    m.lineStyle(1.6, 0x2c2c2c, 1);
    m.beginPath();
    m.moveTo(50, 2);
    m.lineTo(58, 4);
    m.strokePath();
    c.add(m);
    return c;
  },
};
